import React, { useCallback } from 'react';

import { link } from '@aline/routes';
import type { Theme } from '@mui/material';
import { alpha, ButtonBase, Typography } from '@mui/material';
import { AlineTooltip } from 'src/components/generic/AlineTooltip';
import { FileTypeIcon, getFileFormat } from 'src/components/StorableIcon';
import { openLink } from 'src/helpers';
import { makeStyles } from 'tss-react/mui';

import type { ChannelEmailAttachment } from './email-thread';
import { getAttachmentMeta } from './email-thread';
const useStyles = makeStyles()(theme => ({
  card: {
    '&.Mui-focusVisible': {
      borderColor: theme.appColors.primary,
    },
    '&:hover': {
      backgroundColor: theme.appColors.hoverBackgroundSubtle,
    },
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.appColors.ruleSubtle}`,
    borderRadius: 6,
    gap: 12,
    height: 54,
    justifyContent: 'flex-start',
    maxWidth: '100%',
    padding: theme.spacing(1.25, 1.5),
    textAlign: 'left',
    width: 224,
  },
  meta: {
    color: theme.palette.text.secondary,
    fontSize: 10,
    lineHeight: '16px',
  },
  name: {
    lineHeight: '16px',
  },
  text: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  tile: {
    alignItems: 'center',
    borderRadius: 6,
    display: 'flex',
    flexShrink: 0,
    height: 32,
    justifyContent: 'center',
    width: 32,
  },
}));

/** A file from an email, opened as its Aline document. */
export const EmailAttachmentCard = React.memo(function EmailAttachmentCard({
  attachment,
}: {
  attachment: ChannelEmailAttachment;
}) {
  const { classes, theme } = useStyles();
  const { documentId, name } = attachment;
  const mimetype = attachment.mimetype ?? '';
  const open = useCallback(
    () => openLink(link.DocumentDetail({ documentId }), { title: name }),
    [documentId, name]
  );

  return (
    <AlineTooltip placement="top" title={name}>
      <ButtonBase className={classes.card} onClick={open}>
        <span
          className={classes.tile}
          style={{ backgroundColor: getTileBackground(mimetype, theme) }}
        >
          <FileTypeIcon iconProps={{ height: 16, width: 16 }} mimetype={mimetype} />
        </span>
        <span className={classes.text}>
          <Typography noWrap className={classes.name} component="span" variant="caption">
            {name}
          </Typography>
          <Typography className={classes.meta} component="span" variant="caption">
            {getAttachmentMeta(attachment)}
          </Typography>
        </span>
      </ButtonBase>
    </AlineTooltip>
  );
});

function getTileBackground(mimetype: string, theme: Theme): string {
  switch (getFileFormat(mimetype)) {
    case 'pdf':
      return alpha(theme.palette.error.main, 0.1);
    case 'msWord':
      return theme.appColors.selectedBackgroundNav;
    case 'msPPT':
    case 'msExcel':
    case 'image':
    case 'default':
      return theme.appColors.darkBackground;
  }
}
